// ═══════════════════════════════════════════════════════════════
// BenchHelpModal — Méthodologie et résultats ITRIIA BENCH v0.1
// ═══════════════════════════════════════════════════════════════
//
// Modal contextuelle accessible depuis le lien "Source : ITRIIA
// BENCH 2026-04-12" dans le SummarizerOverrideCard ou le picker.
//
// Props:
//   - isOpen: boolean
//   - onClose: () => void
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import { X, FlaskConical, AlertTriangle, Trophy, Ban } from 'lucide-react';
import { MODEL_PROFILES, PICKER_ORDER, EXCLUDED_MODELS, BENCH_RUN } from './modelProfiles';

const ACCENT = '#8b5cf6';

function BenchHelpModal({ isOpen, onClose }) {
    if (!isOpen) return null;

    const profiles = PICKER_ORDER.map(k => MODEL_PROFILES[k]).filter(Boolean);

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 100000,
                background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: '100%', maxWidth: 720, maxHeight: '85vh',
                    background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                    borderRadius: 16, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                }}
            >
                {/* Header */}
                <div style={{
                    padding: '16px 20px', borderBottom: '1px solid var(--card-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FlaskConical size={18} style={{ color: ACCENT }} />
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                                ITRIIA BENCH v0.1
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                Benchmark synthèse — {BENCH_RUN.date}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: 'var(--text-muted)', padding: 4,
                        }}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Scrollable content */}
                <div style={{
                    flex: 1, overflowY: 'auto', padding: '20px',
                    display: 'flex', flexDirection: 'column', gap: 24,
                }}>
                    {/* Méthodologie */}
                    <Section title="Méthodologie" icon={<FlaskConical size={14} />}>
                        <p>
                            Évaluation comparative de 6 modèles LLM sur leur capacité à produire
                            des <strong>synthèses factuelles structurées</strong> de documents techniques,
                            dans le pipeline Bases d'Expertise ITRIIA (ArticleHandler level1 → level2).
                        </p>
                        <InfoGrid items={[
                            { label: 'Document test', value: BENCH_RUN.document },
                            { label: 'Taille', value: `${BENCH_RUN.document_size_kb} KB` },
                            { label: 'Template', value: BENCH_RUN.template },
                            { label: 'Handler', value: BENCH_RUN.handler },
                            { label: 'Sections', value: `${BENCH_RUN.sections_indexed} indexées / ${BENCH_RUN.sections_total} détectées` },
                            { label: 'Évaluateur', value: BENCH_RUN.evaluator },
                        ]} />
                        <p style={{ marginTop: 10 }}>
                            Notation aveugle (labels A-F randomisés) sur 5 axes × 5 points :
                        </p>
                        <ol style={{ margin: '6px 0', paddingLeft: 20, fontSize: 12 }}>
                            <li><strong>Fidélité factuelle</strong> — exactitude des informations extraites</li>
                            <li><strong>Densité informationnelle</strong> — ratio signal/bruit, chiffres précis</li>
                            <li><strong>Structuration</strong> — titres H2, progression logique, lisibilité</li>
                            <li><strong>Style / clarté</strong> — qualité rédactionnelle, accessibilité</li>
                            <li><strong>Neutralité</strong> — absence de jugement, de marketing, de hallucination</li>
                        </ol>
                    </Section>

                    {/* Résultats */}
                    <Section title="Classement" icon={<Trophy size={14} />}>
                        <table style={{
                            width: '100%', borderCollapse: 'collapse',
                            fontSize: 12, lineHeight: 1.5,
                        }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid var(--card-border)' }}>
                                    {['#', 'Modèle', 'Tier', 'Score', 'Coût run', '$/point'].map(h => (
                                        <th key={h} style={{
                                            padding: '6px 8px', textAlign: 'left',
                                            fontWeight: 600, color: 'var(--text-secondary)',
                                            fontSize: 11, textTransform: 'uppercase',
                                        }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {profiles.sort((a, b) => a.bench.rank - b.bench.rank).map((p) => (
                                    <tr key={p.symbolic} style={{
                                        borderBottom: '1px solid var(--card-border)',
                                    }}>
                                        <td style={{ padding: '8px', fontWeight: 600, color: 'var(--text-muted)' }}>
                                            {p.bench.rank}
                                        </td>
                                        <td style={{ padding: '8px', fontWeight: 500, color: 'var(--text)' }}>
                                            {p.label}
                                            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{p.provider}</div>
                                        </td>
                                        <td style={{ padding: '8px' }}>
                                            <span style={{
                                                display: 'inline-block', padding: '2px 8px',
                                                borderRadius: 4, fontSize: 10, fontWeight: 600,
                                                backgroundColor: `${p.tier_color}20`,
                                                border: `1px solid ${p.tier_color}40`,
                                                color: p.tier_color,
                                            }}>
                                                {p.tier_label}
                                            </span>
                                        </td>
                                        <td style={{ padding: '8px', fontWeight: 600, color: p.tier_color }}>
                                            {p.bench.score}/25
                                        </td>
                                        <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>
                                            ${p.bench.cost_run_usd.toFixed(2)}
                                        </td>
                                        <td style={{ padding: '8px', color: 'var(--text-secondary)' }}>
                                            ${p.bench.cost_per_point.toFixed(3)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Score bars visuelles */}
                        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {profiles.sort((a, b) => a.bench.rank - b.bench.rank).map((p) => (
                                <div key={p.symbolic} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ width: 100, fontSize: 11, fontWeight: 500, color: 'var(--text)', textAlign: 'right' }}>
                                        {p.label}
                                    </div>
                                    <div style={{
                                        flex: 1, height: 8, background: 'var(--card-border)',
                                        borderRadius: 4, overflow: 'hidden',
                                    }}>
                                        <div style={{
                                            width: `${(p.bench.score / 25) * 100}%`,
                                            height: '100%', borderRadius: 4,
                                            background: p.tier_color,
                                            transition: 'width 0.3s ease',
                                        }} />
                                    </div>
                                    <div style={{ width: 40, fontSize: 11, fontWeight: 600, color: p.tier_color }}>
                                        {p.bench.score}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* Modèles écartés */}
                    <Section title="Modèles écartés" icon={<Ban size={14} />}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {EXCLUDED_MODELS.map((m) => (
                                <div key={m.id} style={{
                                    padding: '8px 12px', borderRadius: 8,
                                    background: 'rgba(239,68,68,0.06)',
                                    border: '1px solid rgba(239,68,68,0.15)',
                                }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                                        {m.label}
                                        <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6, fontSize: 10 }}>
                                            {m.id}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.4 }}>
                                        {m.reason}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* Limites */}
                    <Section title="Limites connues" icon={<AlertTriangle size={14} />}>
                        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                            <li><strong>1 document testé</strong> — les résultats ne sont pas généralisables à tous les types de contenu</li>
                            <li><strong>1 évaluateur</strong> — biais possible malgré la notation aveugle</li>
                            <li><strong>1 handler</strong> (ArticleHandler) — BookHandler / PaperHandler peuvent produire des classements différents</li>
                            <li><strong>Caps level2</strong> — Kimi/Sonnet/Opus/DeepSeek ont été cappés à 3500 chars au tour 2 (avant fix v2). Seuls Gemini et GPT-5 ont été évalués sans cap.</li>
                            <li><strong>7 sections Weaviate en échec</strong> sur les 84 (unicode qui casse text2vec-openai) — biais identique sur tous les modèles</li>
                            <li><strong>Coûts OpenRouter</strong> — peuvent varier selon le provider routing et les promotions</li>
                        </ul>
                    </Section>

                    {/* Footer */}
                    <div style={{
                        fontSize: 10, color: 'var(--text-muted)', textAlign: 'center',
                        paddingTop: 12, borderTop: '1px solid var(--card-border)',
                    }}>
                        ITRIIA BENCH v0.1 — {BENCH_RUN.limitations}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Sous-composants internes ──────────────────────────────────

function Section({ title, icon, children }) {
    return (
        <div>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
            }}>
                <span style={{ color: ACCENT }}>{icon}</span>
                <h3 style={{
                    margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text)',
                }}>
                    {title}
                </h3>
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
                {children}
            </div>
        </div>
    );
}

function InfoGrid({ items }) {
    return (
        <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '6px 16px', marginTop: 10, padding: '10px 12px',
            background: 'rgba(139,92,246,0.04)', borderRadius: 8,
            border: '1px solid rgba(139,92,246,0.1)',
        }}>
            {items.map((item, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                        {item.label}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text)' }}>
                        {item.value}
                    </span>
                </div>
            ))}
        </div>
    );
}

export default BenchHelpModal;
